import React from "react"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter'
import {dracula} from 'react-syntax-highlighter/dist/esm/styles/prism'

import globalStyles from "../../../../App.module.scss"

const script = `#!/usr/bin/env bash

if [ $# -lt 2 ]; then
  echo "$0 {RELATIVE PATH TO DIRECTORY CONTAINING FOLDERS TO ZIP} {DESTINATION FOLDER ABSOLUTE PATH}"
  exit 0
fi

OS="$(uname -o)"

case "$OS" in
"Darwin")  SEARCH_CMD="find . -type d -d 1"
    ;;
*) SEARCH_CMD="find -maxdepth 1 -type d"
   ;;
esac

echo "$($SEARCH_CMD)"
DEST=$2
CWD=$(pwd) && cd $CWD/$1 && for FOLDER in $($SEARCH_CMD);do
 echo -e "---> $DEST/$FOLDER.zip"
 zip -r $DEST/$FOLDER.zip $FOLDER
done

cd $CWD`

export default function ZipAllFolders() {
    return (
        <div className={globalStyles.article}>
            <h1>Zip each folder separately</h1>
            <p>Takes a directory full of sub-folders and creates <b>one zip per sub-folder</b> — handy for
                archiving or uploading a set of projects, albums or backups individually instead of as a
                single big archive.</p>

            <h3>Requirements</h3>
            <ul>
                <li><code>zip</code> installed</li>
                <li>An existing destination folder — the script does not create it</li>
            </ul>

            <h3>Usage</h3>
            <pre className={globalStyles.code}>
                <code>
                    ./zip-all.sh &lt;relative-path-to-parent-dir&gt; &lt;absolute-path-to-destination&gt;<br/>
                    <br/>
                    <span style={{opacity: 0.7}}># projects/ contains alpha/, beta/ and gamma/</span><br/>
                    ./zip-all.sh projects /home/me/backups<br/>
                    <br/>
                    <span style={{opacity: 0.7}}># /home/me/backups now holds alpha.zip, beta.zip, gamma.zip</span>
                </code>
            </pre>
            <p>With fewer than two arguments it prints the usage line and exits.</p>

            <div className={globalStyles.styledTable}>
                <table style={{minWidth: '450px'}}>
                    <thead>
                    <tr>
                        <th>Argument</th>
                        <th>Notes</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td><code>$1</code></td>
                        <td>Directory containing the folders to zip, <b>relative</b> to where you run the script</td>
                    </tr>
                    <tr>
                        <td><code>$2</code></td>
                        <td>Where the zips are written, <b>absolute</b> — the script <code>cd</code>s into <code>$1</code>,
                            so a relative destination would be resolved from the wrong place</td>
                    </tr>
                    </tbody>
                </table>
            </div>

            <h3>Script</h3>
            <SyntaxHighlighter
                children={script}
                style={dracula}
                PreTag="div"
                language={'bash'}
            />

            <h3>How it works</h3>
            <ol>
                <li><b>Argument check</b> — <code>$#</code> is the number of arguments; fewer than 2 prints
                    the usage line.</li>
                <li><b>Pick the right <code>find</code></b> — <code>uname -o</code> identifies the OS. BSD
                    <code> find</code> on macOS (<code>Darwin</code>) uses <code>-d 1</code> to limit the search
                    to the first level, while the GNU <code>find</code> on Linux uses
                    <code> -maxdepth 1</code>. The command is stored in <code>SEARCH_CMD</code> so the rest of
                    the script is OS-independent.</li>
                <li><b>Move into the parent directory</b> — <code>CWD</code> remembers the starting
                    directory, then <code>cd $CWD/$1</code> moves into the folder that holds the folders to zip.</li>
                <li><b>Loop over the folders</b> — for each one, <code>zip -r</code> archives it recursively
                    as <code>$DEST/&lt;folder&gt;.zip</code>. The folder name is kept as the top-level entry
                    inside the archive, so unzipping recreates the folder rather than spilling its contents.</li>
                <li><b>Go back</b> — the final <code>cd $CWD</code> returns to where you started.</li>
            </ol>

            <h3>Things to know</h3>
            <ul>
                <li>Only <b>folders</b> are zipped. Loose files in the parent directory are ignored.</li>
                <li>Existing zips with the same name are <b>updated</b>, not replaced — <code>zip</code> adds
                    and refreshes entries but doesn't remove files that were deleted from the folder.</li>
                <li>The first <code>echo</code> lists the folders found in the directory you ran the script
                    from, before it changes directory. It is a leftover debug line, not the list of folders
                    that will be zipped.</li>
                <li><b>Folder names with spaces break it.</b> <code>$FOLDER</code> and the output of
                    <code> find</code> are unquoted, so <code>my photos</code> is split into <code>my</code> and
                    <code> photos</code> and both fail with "name not matched".</li>
                <li>On Linux, GNU <code>find -maxdepth 1</code> also matches the starting
                    directory <code>.</code>, which the loop then zips as <code>..zip</code> containing
                    everything.</li>
            </ul>
        </div>
    )
}
